/**
 * TEMP-H-002: Execution Status Strip
 * 
 * Displays live execution metrics: currentStage, progress, attempts, cost, QC status.
 * Real-time updates via control queries hook with formatted display.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { 
  Activity,
  Clock,
  DollarSign,
  TrendingUp,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Minus
} from 'lucide-react';
import { useControlQueries } from '@/hooks/useControlQueries';
import { cn } from '@/utils/cn';
import type { StageType } from '@/types/execution';

interface ExecutionStatusStripProps {
  executionId: string;
  className?: string;
  compact?: boolean;
}

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subValue?: string;
  status?: 'success' | 'warning' | 'error' | 'info';
  className?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  icon,
  label,
  value,
  subValue,
  status = 'info',
  className
}) => {
  const statusColors = {
    success: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    warning: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    error: 'text-red-400 bg-red-500/10 border-red-500/20',
    info: 'text-slate-400 bg-slate-800/30 border-slate-700/30'
  };

  return (
    <div className={cn(
      'flex items-center gap-3 p-3 rounded-lg border backdrop-blur-sm',
      statusColors[status],
      className
    )}>
      <div className="flex-shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">
          {label}
        </div>
        <div className="font-semibold text-slate-200 text-sm">
          {value}
        </div>
        {subValue && (
          <div className="text-xs text-slate-500 mt-0.5">
            {subValue}
          </div>
        )}
      </div>
    </div>
  );
};

// Stage display name mapping
const stageDisplayNames: Record<StageType, string> = {
  script: 'Script',
  vision: 'Vision',
  storyboard: 'Storyboard', 
  shots: 'Shots',
  continuity: 'Continuity',
  frames: 'Frames',
  videos: 'Videos',
  elements: 'Elements',
  qc: 'QC',
  assemble: 'Assembly',
  music: 'Music',
  publish: 'Publish',
  music_master: 'Music Master',
  music_sidechain: 'Music Sidechain',
  export_preview: 'Export Preview'
};

const ExecutionStatusStrip: React.FC<ExecutionStatusStripProps> = ({
  executionId,
  className,
  compact = false
}) => {
  const { data: queries, isLoading, error } = useControlQueries(executionId);

  // Format progress percentage
  const formatProgress = (progress: number): string => {
    return `${Math.round(progress * 100)}%`;
  };

  // Format cost with appropriate currency
  const formatCost = (cost: number): string => {
    if (cost < 0.01) return '< $0.01';
    return `$${cost.toFixed(2)}`;
  };

  // Get QC status icon and color
  const getQCStatusDisplay = (status: string) => {
    switch (status) {
      case 'pass':
        return {
          icon: <CheckCircle className="w-4 h-4" />,
          status: 'success' as const,
          label: 'Passed'
        };
      case 'borderline':
        return {
          icon: <AlertTriangle className="w-4 h-4" />,
          status: 'warning' as const,
          label: 'Borderline'
        };
      case 'fail':
        return {
          icon: <XCircle className="w-4 h-4" />,
          status: 'error' as const,
          label: 'Failed'
        };
      case 'pending':
      default:
        return {
          icon: <Minus className="w-4 h-4" />,
          status: 'info' as const,
          label: 'Pending'
        };
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={cn(
        'flex items-center gap-4 p-4 bg-slate-800/30 border border-slate-700/30 rounded-lg',
        className
      )}>
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        <span className="text-slate-400 text-sm">Loading execution status...</span>
      </div>
    );
  }

  // Error state
  if (error || !queries) {
    return (
      <div className={cn(
        'flex items-center gap-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg',
        className
      )}>
        <XCircle className="w-5 h-5 text-red-400" />
        <span className="text-red-300 text-sm">
          Failed to load execution status
        </span>
      </div>
    );
  }

  const qcDisplay = getQCStatusDisplay(queries.qcStatus);

  // Compact layout for smaller spaces
  if (compact) {
    return (
      <div className={cn(
        'grid grid-cols-2 md:grid-cols-4 gap-2',
        className
      )}>
        <MetricCard
          icon={<Activity className="w-4 h-4" />}
          label="Stage"
          value={queries.currentStage ? stageDisplayNames[queries.currentStage] : 'None'}
        />
        <MetricCard
          icon={<TrendingUp className="w-4 h-4" />}
          label="Progress"
          value={formatProgress(queries.progress)}
        />
        <MetricCard
          icon={<DollarSign className="w-4 h-4" />}
          label="Cost"
          value={formatCost(queries.costSoFar)}
        />
        <MetricCard
          icon={qcDisplay.icon}
          label="QC"
          value={qcDisplay.label}
          status={qcDisplay.status}
        />
      </div>
    );
  }

  // Full layout
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4',
        className
      )}
    >
      {/* Current Stage */}
      <MetricCard
        icon={<Activity className="w-5 h-5" />}
        label="Current Stage"
        value={queries.currentStage ? stageDisplayNames[queries.currentStage] : 'Idle'}
        subValue={queries.currentStage ? 'Active' : 'No active stage'}
        status={queries.currentStage ? 'success' : 'info'}
      />

      {/* Progress */}
      <MetricCard
        icon={<TrendingUp className="w-5 h-5" />}
        label="Progress"
        value={formatProgress(queries.progress)}
        subValue="Overall completion"
        status={queries.progress > 0.8 ? 'success' : queries.progress > 0.4 ? 'warning' : 'info'}
      />

      {/* Attempts */}
      <MetricCard
        icon={<Clock className="w-5 h-5" />}
        label="Attempts"
        value={queries.attempts}
        subValue="Current stage"
        status={queries.attempts > 3 ? 'warning' : 'info'}
      />

      {/* Cost */}
      <MetricCard
        icon={<DollarSign className="w-5 h-5" />}
        label="Cost So Far"
        value={formatCost(queries.costSoFar)}
        subValue="USD spent"
        status={queries.costSoFar > 10 ? 'warning' : 'info'}
      />

      {/* QC Status */}
      <MetricCard
        icon={qcDisplay.icon}
        label="QC Status"
        value={qcDisplay.label}
        subValue="Quality control"
        status={qcDisplay.status}
      />
    </motion.div>
  );
};

export default ExecutionStatusStrip;